import { useEffect, useRef, useState } from 'react';
import { rawApi } from '../lib/api';
import { extractScatter } from '../lib/raw-scatter3d';
import { useOptionalToast } from '../components/Toast';

const EMPTY = { scatters: {}, errors: {} };

/**
 * Fetches the apparent-resistivity pseudo-positions of each ticked raw survey once
 * (GET /api/datasets/{id}/raw/pseudosection, georeferenced) and keeps them as compact arrays:
 * { scatters: id -> extract, errors: id -> message }. A failure sets an error and raises one toast.
 */
export default function useRawScatter(datasets) {
  const [state, setState] = useState(EMPTY);
  const requested = useRef(new Set());
  const toast = useOptionalToast();
  const showToast = toast?.showToast;

  useEffect(() => {
    datasets.forEach((d) => {
      if (requested.current.has(d.id)) return;
      requested.current.add(d.id);
      const fail = (detail) => {
        setState((prev) => ({ ...prev, errors: { ...prev.errors, [d.id]: detail } }));
        if (showToast) showToast(`Could not load the scatter of ${d.name || d.filename || d.id}: ${detail}`, 'error', 5000);
      };
      rawApi.pseudosectionAll(d.id)
        .then((res) => {
          const ext = extractScatter(res.data);
          if (!ext.count) { fail('no readings with apparent resistivity'); return; }
          setState((prev) => ({ ...prev, scatters: { ...prev.scatters, [d.id]: ext } }));
        })
        .catch((err) => {
          const detail = err?.response?.data?.detail;
          fail((typeof detail === 'string' && detail) || err?.message || 'request failed');
        });
    });
  }, [datasets, showToast]);

  return state;
}
